/* eslint-disable ngrx/avoid-combining-selectors */
/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
/* eslint-disable ngrx/avoid-dispatching-multiple-actions-sequentially */
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
// Store
import { Store } from '@ngrx/store';
import dateFormat from 'date-fns/format';
import { forEach as _forEach, get as _get, has as _has, merge as _merge } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions } from '@amfam/billing/one-time-pay/feature';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentMethod } from '@amfam/billing/payment-method/util';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import {
  ScheduledPaymentActions,
  ScheduledPaymentSelectors
} from '@amfam/billing/schedulepayment/data-access';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import { AnalyticsActions } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillAccountModel,
  BillAccountPaymentsScheduleRow,
  BillingAnalytics,
  BillingPaymentPaths,
  ONLINE_BILLING,
  PaymentStatus,
  ScheduledPayment,
  SchedulePaymentPayloadModel
} from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  UtilService,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DockingBarService,
  DsModalService,
  DsTableUtilService,
  LoadingSpinnerService,
  SidebarService
} from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-payments-scheduled',
  templateUrl: './bill-account-payments-scheduled.component.html',
  styleUrls: ['./bill-account-payments-scheduled.component.scss'],
  standalone: false
})
export class PaymentsScheduledComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  // Optional Input. When present filter the scheduled payments to show only those including this account
  @Input() billAccountNumber;
  @Input() isStandAlone = false;
  @Output() accordionPaySilentRegFailureEvent = new EventEmitter();
  @Input() tableOnly = false;

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('scheduledReferenceCellTpl', { static: true })
  scheduledReferenceCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentAccountCellTpl', { static: true }) paymentAccountCellTpl: TemplateRef<unknown>;
  @ViewChild('paymentDetailsCellTpl', { static: true }) paymentDetailsCellTpl: TemplateRef<unknown>;
  @ViewChild('editCellTpl', { static: true }) editCellTpl: TemplateRef<unknown>;
  @ViewChild('scheduledPaymentsTable') table;

  registeredBillAccounts: BillAccount[] = [];
  scheduledPayments: ScheduledPayment[] = [];
  paymentMethods: PaymentMethod[] = [];
  sidebarPresent = false;
  disableAddButton = false;
  isMyAccountAdmin: boolean;

  dataLoading: Observable<boolean>;

  // Drives the detail row in the case of a bundled payment
  selectedScheduledPayment: BillAccountPaymentsScheduleRow;

  // Table init vars
  rows = [];
  columns = [];
  cssClasses = 'material noshadow expandable payment-scheduled';
  footerHeight = '';
  pageLimit = 5;

  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;
  deletePendingPaymentModalConfig: AlertModalConfig = {
    headerText: 'Are you sure you want to delete?',
    bodyText:
      'You’ll need to make sure you set up another payment to stay current on your account.',
    modalId: 'deletePendingPaymentModal',
    ctaConfig: {
      primaryButtonName: 'Delete',
      primaryButtonDestructive: true,
      tertiaryButtonName: 'Cancel',
      fullWidthButtons: true
    }
  };
  loading: Observable<boolean>;
  loadingMsgText = 'Looking for billing and payment data';
  actionInFlight: boolean;
  otpBillAccounts: BillAccount[];
  private userRegisteredBillAccounts: BillAccount[];
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private utilService: UtilService,
    private store: Store,
    private spinner: LoadingSpinnerService,
    private win: WindowRef,
    private tableUtilService: DsTableUtilService,
    private sidebarService: SidebarService,
    private transmuteService: BillAccountTransmuteService,
    private dockingService: DockingBarService,
    private applicationService: ApplicationService,
    private feature: FeatureFlagService,
    private modalService: DsModalService
  ) {}

  ngOnInit() {
    this.isMyAccountAdmin = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
    if (this.isMyAccountAdmin) {
      this.store.dispatch(
        AnalyticsActions.sendAnalytic({
          options: {
            page: BillingAnalytics.viewAllPaymentsAdminAnalytic
          }
        })
      );
    }
    if (this.isStandAlone) {
      this.dockingService.registerHeading('View Payments');
      if (this.applicationService.isApp(Applications.MYACCOUNT_ADMIN)) {
        this.store
          .select(userQuery.selectFirstAndLastName)
          .pipe(
            filter(name => !!name),
            take(1)
          )
          .subscribe(name => {
            this.dockingService.registerSecondaryHeading(`${name}`);
          });
      }
    }

    // Kill any rogue loading spinner activity
    this.spinner.stop();

    // Build table
    this.buildSchedulePaymentTable();

    // Async Pipe aggregate loading information
    this.dataLoading = combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsAnyLoading),
      this.store.select(PaymentMethodSelectors.selectLoading),
      this.store.select(PaymentAccountSelectors.selectPaymentAccountsAnyLoading),
      (
        billAccountsLoading,
        scheduledPaymentsLoading,
        paymentMethodsLoading,
        paymentAccountsLoading
      ) =>
        billAccountsLoading ||
        scheduledPaymentsLoading ||
        paymentMethodsLoading ||
        paymentAccountsLoading
    );

    combineLatest(
      this.store.select(ScheduledPaymentSelectors.selectScheduledPayments),
      this.store.select(BillAccountsSelectors.selectUserRegisteredBillAccounts),
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay),
      this.store.select(PaymentAccountSelectors.selectPaymentAccountsExpiredLast),
      this.store.select(PaymentMethodSelectors.selectPaymentMethodsSortedByExpirationStatus),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsError),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsAnyLoading),
      this.store.select(PaymentAccountSelectors.selectPaymentAccountsAnyLoading),
      this.store.select(PaymentMethodSelectors.selectLoading),
      (
        scheduledPayments,
        userRegisteredBillAccounts,
        otpBillAccounts,
        _paymentAccounts,
        paymentMethods,
        scheduledPaymentError,
        billAcctsLoading,
        scheduledPaymentsLoading,
        paymentAccountsLoading,
        paymentMethodsLoading
      ) => ({
        scheduledPayments: scheduledPayments,
        userRegisteredBillAccounts: userRegisteredBillAccounts,
        otpBillAccounts: otpBillAccounts,
        paymentAccounts: paymentMethods,
        loading:
          billAcctsLoading ||
          scheduledPaymentsLoading ||
          paymentAccountsLoading ||
          paymentMethodsLoading,
        error: scheduledPaymentError
      })
    )
      .pipe(
        filter(state => !state.loading && !state.error),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.userRegisteredBillAccounts = state.userRegisteredBillAccounts;
        this.otpBillAccounts = state.otpBillAccounts;
        // Get the registered bill accounts
        this.registeredBillAccounts = [];
        _forEach(this.userRegisteredBillAccounts, billAcct => {
          if (_get(billAcct, 'billingMethod') === ONLINE_BILLING) {
            this.registeredBillAccounts.push(billAcct);
          }
        });

        // Set the payment methods for use later.
        this.paymentMethods = state.paymentAccounts;

        // This is used by the edit detail row component
        this.scheduledPayments = state.scheduledPayments;

        this.buildRowsList();

        // Determine if we should enable / disable the add button
        this.disableAddButton = this.determineDisableAddButton();

        // Determine if the table should render the pagination controls based on rows
        if (this.rows && this.rows.length > this.pageLimit) {
          this.footerHeight = 'auto';
        } else {
          this.footerHeight = '';
        }
      });
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      this.sidebarService.sidebarPresent$
        .pipe(takeUntil(this.stop$))
        .subscribe(present => (this.sidebarPresent = present));
    }, 0);
  }

  // Lifecycle method, listen for changes in bill account to update filter
  ngOnChanges(changes: SimpleChanges) {
    // eslint-disable-next-line no-prototype-builtins
    if (changes.hasOwnProperty('billAccountNumber')) {
      if (
        changes.billAccountNumber.previousValue !== undefined &&
        changes.billAccountNumber.previousValue !== changes.billAccountNumber.currentValue
      ) {
        this.buildRowsList();
      }
    }
    this.disableAddButton = this.determineDisableAddButton();
  }

  // Kill subscriptions on destroy
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  onCloseRow(row?: BillAccountPaymentsScheduleRow) {
    if (this.table) {
      this.tableUtilService.removeExpandedClass(this.table);
    }

    if (row) {
      this.toggleRowDetails(row);
    } else if (_has(this.table, 'rowDetail')) {
      this.table.rowDetail.collapseAllRows();
    }
  }

  onEditEvent(row: BillAccountPaymentsScheduleRow) {
    const paymentId = _get(row, 'paymentDetails.paymentId');
    this.store.dispatch(OneTimePayActions.routeToReviewForEdit({ paymentId }));
  }

  onDeleteEvent() {
    this.modalService.open('deletePendingPaymentModal');
  }

  removePendingPayment(row: BillAccountPaymentsScheduleRow) {
    this.modalService.closeAll();
    const inputDate = dateFormat(row.paymentDetails.receivedDate, 'YYYY-MM-DD');
    // generate correlation id
    const correlationId = this.utilService.generateId();
    const paymentId = row.paymentDetails.paymentId;
    const paymentInfoObj: SchedulePaymentPayloadModel = {
      payment: {
        receivedDate: inputDate,
        totalPaymentAmount: Number(_get(row, 'paymentDetails.totalPaymentAmount', 0)).toFixed(2),
        paymentAccount: _get(row, 'paymentDetails.paymentAccount'),
        billAccounts: _get(row, 'paymentDetails.billAccounts')
      },
      paymentId: paymentId,
      correlationId: correlationId
    };
    this.actionInFlight = true;
    this.spinner.start({
      blockActions: true
    });

    this.store.dispatch(ScheduledPaymentActions.scheduledPaymentsDelete(paymentInfoObj));
    this.store.dispatch(OneTimePayActions.storePaymentId({ paymentId }));
  }

  cancel() {
    this.modalService.close('deletePendingPaymentModal');
  }

  onScheduleNameClickEvent(row: BillAccountPaymentsScheduleRow) {
    this.selectedScheduledPayment = row;
    this.table.rowDetail.collapseAllRows();
    this.table.rowDetail.toggleExpandRow(row);
  }

  addScheduledPaymentClick() {
    this.store.dispatch(
      AnalyticsActions.sendAnalytic({
        options: {
          button: BillingAnalytics.schedulePaymentClick
        }
      })
    );
    // Account is unregistered, do silent registation
    this.spinner.start({ blockActions: true });
    if (this.billAccountNumber) {
      const matchingBillAccount = this.userRegisteredBillAccounts.find(
        billAcct => billAcct.billAccountNumber === this.billAccountNumber
      );
      // Go to payment flow regardless of whether account is registered
      this.store.dispatch(
        pendingPaymentActions.AddPendingPaymentAction({
          billAccountNumber: this.billAccountNumber
        })
      );
      // If account is registered
      if (
        _get(matchingBillAccount, 'billingMethod') === ONLINE_BILLING &&
        !matchingBillAccount.registeredElsewhere
      ) {
        // Emit add scheduled payment action and route to standalone payment widget
        // ONE TIME PAYMENT ENTRYPOINT
        if (this.feature.isEnabled('simplified_payment')) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
            })
          );
        } else {
          if (this.otpBillAccounts.length === 1) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          }
        }
      } else {
        const correlationId = this.utilService.generateId();
        const silentRegistrationPayload = this.transmuteService.buildSilentRegistrationPayload(
          [this.billAccountNumber],
          correlationId
        );
        this.store.dispatch(
          BillAccountActions.billAccountSilentRegistration(silentRegistrationPayload)
        );
        this.store
          .select(BillAccountsSelectors.selectBillAccounts)
          .pipe(
            mergeMap(billAccounts => billAccounts),
            withLatestFrom(
              this.store.select(BillAccountsSelectors.selectBillAccountNotificationsEntities)
            ),
            filter(
              ([billingAccount, matchedBillAccountNotificationState]) =>
                matchedBillAccountNotificationState[billingAccount.billAccountNumber]
                  .correlationId === correlationId &&
                (!!billingAccount.registrationDate ||
                  !!matchedBillAccountNotificationState[billingAccount.billAccountNumber]
                    .updateRegistrationError)
            ),
            take(1)
          )
          .subscribe(([billAccount]) => {
            this.spinner.stop();
            if (_get(billAccount, 'registrationDate')) {
              // ONE TIME PAYMENT ENTRYPOINT
              if (this.feature.isEnabled('simplified_payment')) {
                this.store.dispatch(
                  fromRouterActions.Go({
                    path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                  })
                );
              } else {
                if (this.otpBillAccounts.length === 1) {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                    })
                  );
                } else {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                    })
                  );
                }
              }
            } else {
              this.accordionPaySilentRegFailureEvent.emit();
            }
          });
      }
    } else {
      // If we are on the section page, scoll to the top. Otherwise, route to the section page and scroll to the top
      // ONE TIME PAYMENT ENTRYPOINT
      if (this.feature.isEnabled('simplified_payment')) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
          })
        );
      } else {
        if (this.otpBillAccounts.length === 1) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
            })
          );
        } else {
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
            })
          );
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toggleRowDetails(event: any) {
    if (event && event.row) {
      this.table.rowDetail.toggleExpandRow(event.row);
    } else if (event) {
      this.table.rowDetail.toggleExpandRow(event);
    }
  }

  private buildSchedulePaymentTable() {
    this.columns = [
      {
        cellTemplate: this.scheduledReferenceCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Billing Account',
        shortName: 'Account',
        prop: 'singleAccount',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 140
      },
      {
        cellTemplate: this.paymentAccountCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Payment Method',
        shortName: 'Method',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 200
      },
      {
        cellTemplate: this.paymentDetailsCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Amount',
        prop: 'paymentDetails',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 120
      },
      {
        cellTemplate: this.editCellTpl,
        headerTemplate: this.headerCellTpl,
        name: ' ',
        draggable: false,
        resizeable: false,
        sortable: false,
        canAutoResize: false,
        width: 180
      }
    ];
  }

  // Iterate the scheduled payments, filter by bill account number if we have one, format each row to needed object shape.
  private buildRowsList() {
    this.rows = [];
    // Show only payments with 'scheduled' payment status
    let filteredPayments = this.scheduledPayments.filter(
      payment => payment.paymentStatus === PaymentStatus.SCHEDULED
    );
    // If we have a billAccountNumber as input, filter to only show payments for that account
    if (this.billAccountNumber) {
      filteredPayments = filteredPayments.filter(payment => {
        let matchingBillAccount: BillAccountModel;
        // In case the payment came back as empty, we need to check before accessing its values.
        if (payment) {
          matchingBillAccount = payment.billAccounts.find(
            billAccountPayment => billAccountPayment.billAccountNumber === this.billAccountNumber
          );
        }
        return matchingBillAccount ? true : false;
      });
    }

    _forEach(filteredPayments, scheduledPayment => {
      if (scheduledPayment && scheduledPayment.paymentAccount) {
        // replace the payment account with the whole object.
        const paymentAccount = this.paymentMethods.find(
          method => method.nickName === _get(scheduledPayment, 'paymentAccount.nickName', '')
        );
        const newScheduledPayment = Object.assign({}, scheduledPayment, {
          paymentAccount: paymentAccount
        });

        // AS: Before the payload can be pushed to the rows we need to massage the data to be in certain structure.
        const formattedRes = this.formatSingleRow(newScheduledPayment);

        if (formattedRes) {
          this.rows.push({
            nickName: formattedRes.nickName,
            bundledPayments: formattedRes.bundledPayments,
            singleAccount: {
              singleAccountNumber: formattedRes.singleAccountNumber,
              singleNickName: formattedRes.nickName
            },
            paymentAccount: paymentAccount,
            paymentDetails: newScheduledPayment,
            paymentType: scheduledPayment.paymentType,
            paymentAccountNickName: scheduledPayment.paymentAccount
              ? _get(scheduledPayment, 'paymentAccount.nickName')
              : ''
          });
        }
      }
    });
  }

  // Massage individual scheduled payment entries to the structure to be used in the table rows.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatSingleRow(payload: any): any {
    // Determine the overall Row NickName.
    let nickName = 'Bundled Payment';
    let singleAccountNumber: string;
    if (_has(payload, 'billAccounts') && payload.billAccounts.length === 1) {
      const matchingBillAccount = this.registeredBillAccounts.find(
        billAcct => billAcct.billAccountNumber === payload.billAccounts[0].billAccountNumber
      );
      if (
        _get(matchingBillAccount, 'billingMethod') === ONLINE_BILLING &&
        !matchingBillAccount.registeredElsewhere
      ) {
        nickName = _get(matchingBillAccount, 'billingPreferences.accountNickName');
        singleAccountNumber = matchingBillAccount.billAccountNumber;
      } else {
        // The account is no longer registered, don't show it.
        return null;
      }
    }

    const arrBillAccounts: BillAccount[] = [];
    // Pull out the bill account details for all the rest of the bill accounts under this payment.
    _forEach(payload.billAccounts, (billAccountPayment, index) => {
      const matchingBillAccount = this.registeredBillAccounts.find(
        billAcct => billAcct.billAccountNumber === billAccountPayment.billAccountNumber
      );

      if (matchingBillAccount) {
        arrBillAccounts.push(
          _merge(
            {},
            {
              nickName: _get(matchingBillAccount, 'billingPreferences.accountNickName'),
              accountBalance: matchingBillAccount.accountBalance,
              minimumAmountDue: matchingBillAccount.minimumAmountDue,
              paymentDueDate: matchingBillAccount.paymentDueDate,
              fullPayBalance: matchingBillAccount.fullPayBalance
            },
            payload.billAccounts[index]
          )
        );
      } else {
        // The account is no longer registered, don't show it.
        return null;
      }
    });

    if (arrBillAccounts.length) {
      payload.billAccounts = arrBillAccounts;
    }

    return {
      nickName: nickName,
      singleAccountNumber: singleAccountNumber,
      bundledPayments: payload.billAccounts
    };
  }

  private determineDisableAddButton(): boolean {
    // quick check to determine if we should show/hide the add button based on single bill account balance
    if (this.billAccountNumber) {
      const matchingBillAccount = this.registeredBillAccounts.find(
        billAccount => billAccount && billAccount.billAccountNumber === this.billAccountNumber
      );
      if (
        (matchingBillAccount &&
          matchingBillAccount.accountBalance &&
          Number(matchingBillAccount.accountBalance) <= 0) ||
        this.otpBillAccounts?.length === 0
      ) {
        return true;
      }
    } else if (this.otpBillAccounts.length === 0) {
      return true;
    } else {
      return false;
    }
  }
}
